'use client';

import { T } from '@/i18n/LocaleProvider';
import { MONO_FONT } from '@/theme';
import { useState, useRef, useCallback, useEffect } from 'react';
import { panelFadeSwap, panelFadeIn, gsap } from '@/utils/gsap';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { PageSection } from '@/components';
import styles from './Modules.module.scss';

const TABS = [
  {
    id: 'm1',
    num: 'M1',
    label: 'Monitoreo · cobertura por motor',
    title: 'Cobertura por motor',
    meta: 'M1 · Ejecuta 200 prompts/sem por motor',
    desc: 'Distribución del share-of-voice de tu marca en consultas relevantes para tu categoría. Calculado contra los 5 motores con muestreo semanal y rotación de prompts.',
    content: 'bars',
    bars: [
      { name: 'ChatGPT', value: 68 },
      { name: 'Perplexity', value: 54 },
      { name: 'Gemini', value: 41 },
      { name: 'Claude', value: 36 },
      { name: 'Copilot', value: 29 },
      { name: 'Google AIO', value: 22 },
    ],
  },
  {
    id: 'm2',
    num: 'M2',
    label: 'Diagnóstico · 4 dimensiones',
    title: 'Diagnóstico baseline',
    meta: 'M2 · Diagnóstico 0–100 en 4 dimensiones',
    desc: 'Identidad firmográfica, reputación externa verificable, cobertura editorial y conversación social. Cada brecha se etiqueta con severidad y bloqueante de avance.',
    content: 'issues',
    issues: [
      {
        title: 'Schema.org incompleto',
        sub: 'Falta SoftwareApplication + AggregateRating. Los LLMs no resuelven entidad.',
        tag: 'Identidad · Crítico',
        level: 'crit',
      },
      {
        title: 'Sin llms.txt publicado',
        sub: 'Standard emergente. Hoy lo leen Perplexity y Anthropic. Tiempo estimado: 2 h.',
        tag: 'Identidad · Alto',
        level: 'warn',
      },
      {
        title: 'Trustpilot sin perfil reclamado',
        sub: '11 reseñas orgánicas, ninguna respondida. Quick win con impacto cita en 3 motores.',
        tag: 'Reputación · Medio',
        level: '',
      },
      {
        title: 'Wikipedia ES sin entrada',
        sub: 'Bloquea citas en Gemini y Google AIO. Requiere editor con >100 ediciones previas.',
        tag: 'Editorial · Medio',
        level: '',
      },
      {
        title: 'Sin presencia en subreddits sectoriales',
        sub: 'r/marketing · r/SaaS · r/spanish. Conversación pública es input de muestreo.',
        tag: 'Social · Bajo',
        level: '',
      },
    ],
  },
  {
    id: 'm3',
    num: 'M3',
    label: 'Playbook · acciones priorizadas',
    title: 'Playbook activado',
    meta: 'M3 · 12 acciones seleccionadas de 25',
    desc: 'La biblioteca completa son 25 acciones en 8 familias. La plataforma selecciona el subset adecuado para tu arquetipo de marca y secuencia 6 meses.',
    content: 'plays',
    plays: [
      {
        code: 'J03 · IDENTIDAD',
        name: 'Schema.org completo',
        meta: '8 h · impacto +14 pts',
        high: true,
      },
      {
        code: 'J05 · TÉCNICO',
        name: 'Publicar llms.txt',
        meta: '2 h · impacto +6 pts',
        high: true,
      },
      {
        code: 'J08 · REPUTACIÓN',
        name: 'Plan de reseñas G2',
        meta: '24 h · impacto +11 pts',
        high: false,
      },
      {
        code: 'J11 · EDITORIAL',
        name: 'Wiki ES estructurada',
        meta: '40 h · impacto +9 pts',
        high: false,
      },
      {
        code: 'J14 · CONTENIDO',
        name: 'Hub técnico AEO',
        meta: '12 posts · +18 pts',
        high: false,
      },
      {
        code: 'J21 · SOCIAL',
        name: 'LinkedIn CEO firmado',
        meta: '2 posts/sem · +7 pts',
        high: false,
      },
    ],
  },
  {
    id: 'm4',
    num: 'M4',
    label: 'Posicionamiento · curva de citas',
    title: 'Curva de citas acumuladas',
    meta: 'M4 · Últimas 12 semanas',
    desc: 'La métrica que importa: citas verificables que sobreviven al re-entrenamiento. Cruzamos cobertura propia con menciones de terceros estructuradas.',
    content: 'trend',
  },
];

function PanelContent({ tab }) {
  const barsRef = useRef(null);
  const valRefs = useRef([]);

  useEffect(() => {
    if (tab.content !== 'bars' || !barsRef.current) return;
    const fills = barsRef.current.querySelectorAll(`.${styles.barFill}`);
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: barsRef.current,
        start: 'top 80%',
        once: true,
      },
    });
    Array.from(fills).forEach((fill, i) => {
      const valEl = valRefs.current[i];
      const target = tab.bars[i].value;
      const obj = { v: 0 };
      tl.fromTo(
        obj,
        { v: 0 },
        {
          v: target,
          duration: 0.7,
          ease: 'power2.out',
          onUpdate() {
            fill.style.width = `${obj.v}%`;
            if (valEl) valEl.textContent = `${Math.round(obj.v)}%`;
          },
        },
        i * 0.07
      );
    });
    return () => tl.kill();
  }, [tab]);

  if (tab.content === 'bars') {
    return (
      <div className={styles.bars} ref={barsRef}>
        {tab.bars.map((b, i) => (
          <div key={b.name} className={styles.bar}>
            <div className={styles.barName}>
              <T>{b.name}</T>
            </div>
            <div className={styles.barTrack}>
              {/* width starts at 0 — GSAP anima al valor real */}
              <div className={styles.barFill} />
            </div>
            <div
              className={styles.barVal}
              ref={(el) => {
                valRefs.current[i] = el;
              }}
            >
              <T>{b.value}</T>%
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tab.content === 'issues') {
    return (
      <div className={styles.issues}>
        {tab.issues.map((issue) => (
          <div
            key={issue.title}
            className={`${styles.issue} ${issue.level ? styles[issue.level] : ''}`}
          >
            <div>
              <div className={styles.issueTitle}>
                <T>{issue.title}</T>
              </div>
              <div className={styles.issueSub}>
                <T>{issue.sub}</T>
              </div>
            </div>
            <span className={styles.issueTag}>
              <T>{issue.tag}</T>
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (tab.content === 'plays') {
    return (
      <div className={styles.plays}>
        {tab.plays.map((play) => (
          <div key={play.code} className={styles.play}>
            <div className={styles.playHead}>
              <span className={styles.playCode}>
                <T>{play.code}</T>
              </span>
              <span
                className={`${styles.playPrio} ${play.high ? styles.high : ''}`}
              >
                <T>{play.high ? 'PRIO 1' : 'PRIO 2'}</T>
              </span>
            </div>
            <div className={styles.playName}>
              <T>{play.name}</T>
            </div>
            <div className={styles.playMeta}>
              <T>{play.meta}</T>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tab.content === 'trend') {
    return (
      <div className={styles.trendWrap}>
        <div className={styles.trendHead}>
          <div>
            <div className={styles.trendLabel}>
              <T>CITAS ACUMULADAS</T>
            </div>
            <div className={styles.trendVal}>
              412{' '}
              <span className={styles.delta}>
                <T>+86 vs prev</T>
              </span>
            </div>
          </div>
          <div>
            <div className={styles.trendLabel}>
              <T>SHARE-OF-VOICE</T>
            </div>
            <div className={styles.trendVal}>
              34%{' '}
              <span className={styles.delta}>
                <T>+9 pts</T>
              </span>
            </div>
          </div>
        </div>
        <svg
          viewBox="0 0 600 140"
          preserveAspectRatio="none"
          className={styles.trendSvg}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="tg" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#009f8b" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#009f8b" stopOpacity="0" />
            </linearGradient>
          </defs>
          <g stroke="#e2e5e3" strokeWidth="1">
            <line x1="0" y1="35" x2="600" y2="35" />
            <line x1="0" y1="70" x2="600" y2="70" />
            <line x1="0" y1="105" x2="600" y2="105" />
          </g>
          <path
            d="M 0,115 L 50,108 L 100,102 L 150,95 L 200,88 L 250,76 L 300,70 L 350,58 L 400,50 L 450,42 L 500,30 L 550,22 L 600,18 L 600,140 L 0,140 Z"
            fill="url(#tg)"
          />
          <path
            d="M 0,115 L 50,108 L 100,102 L 150,95 L 200,88 L 250,76 L 300,70 L 350,58 L 400,50 L 450,42 L 500,30 L 550,22 L 600,18"
            fill="none"
            stroke="#009f8b"
            strokeWidth="2"
          />
          <g fill="#009f8b">
            {[0, 100, 200, 300, 400, 500, 600].map((x, i) => {
              const ys = [115, 102, 88, 70, 50, 30, 18];
              return <circle key={x} cx={x} cy={ys[i]} r={i === 6 ? 4 : 3} />;
            })}
          </g>
        </svg>
        <div className={styles.trendAxis}>
          {['S01', 'S03', 'S05', 'S07', 'S09', 'S11', 'HOY'].map((l) => (
            <span key={l}>
              <T>{l}</T>
            </span>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

export default function Modules() {
  const [active, setActive] = useState(0);
  const panelRef = useRef(null);
  const tab = TABS[active];

  const handleTabChange = useCallback(
    async (_, v) => {
      if (v === active) return;
      await panelFadeSwap(panelRef.current);
      setActive(v);
      panelFadeIn(panelRef.current);
    },
    [active]
  );

  return (
    <PageSection
      id="modulos"
      compact
      eyebrow="03 / Módulos"
      title={
        <>
          <T>Cuatro módulos.</T>{' '}
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <span className="accent">
            <T>Un solo</T>
          </span>
          <T> flujo de trabajo.</T>
        </>
      }
      lede="Diseñados para ejecutar el ciclo AEO de extremo a extremo: del rastreo bruto al roadmap firmado por el cliente. Selecciona un módulo para ver qué entrega."
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
          borderTop: '1px solid #e2e5e3',
        }}
      >
        {/* Tabs sidebar */}
        <Tabs
          orientation="vertical"
          value={active}
          onChange={handleTabChange}
          sx={{
            mt: 4,
            borderRight: { md: '1px solid #e2e5e3' },
            '.MuiTabs-indicator': { display: 'none' },
            '.MuiTabs-flexContainer': { alignItems: 'stretch' },
            borderBottom: 0,
          }}
          TabScrollButtonProps={{ sx: { display: 'none' } }}
        >
          {TABS.map((t, i) => (
            <Tab
              key={t.id}
              disableRipple
              sx={{
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: '32px 1fr 20px',
                justifyContent: 'flex-start',
                minHeight: 'unset',
                padding: '18px 20px 18px 0',
                textAlign: 'left',
                textTransform: 'none',
                color: 'text.muted',
                '&.Mui-selected': {
                  color: 'text.primary',
                  '.tab-num': { color: 'text.accent' },
                  '.tab-name': { fontWeight: 600 },
                  '.tab-arrow': {
                    color: 'text.accent',
                    transform: 'translateX(2px)',
                  },
                },
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { color: 'text.primary' },
              }}
              label={
                <>
                  <Box
                    className="tab-num"
                    component="span"
                    sx={{
                      fontFamily: MONO_FONT,
                      fontSize: '11px',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: i === active ? 'text.accent' : 'grey.600',
                      fontWeight: 600,
                    }}
                  >
                    <T>{t.num}</T>
                  </Box>
                  <Box
                    className="tab-name"
                    component="span"
                    sx={{
                      fontSize: '13px',
                      letterSpacing: 'normal',
                      fontWeight: i === active ? 600 : 400,
                      fontFamily: 'default',
                    }}
                  >
                    <T>{t.label}</T>
                  </Box>
                  <Box
                    className="tab-arrow"
                    component="span"
                    aria-hidden="true"
                    sx={{
                      fontSize: '16px',
                      color: i === active ? 'text.accent' : 'grey.300',
                      transition: 'transform 0.15s, color 0.15s',
                      transform: i === active ? 'translateX(2px)' : 'none',
                      display: { xs: 'none', md: 'inline' },
                    }}
                  >
                    →
                  </Box>
                </>
              }
            />
          ))}
        </Tabs>

        {/* Panel — ref para cross-fade GSAP al cambiar tab */}
        <Box
          ref={panelRef}
          sx={{ p: { xs: '24px 0 0', md: '32px 0 32px 40px' } }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'baseline' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'grey.200',
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 600,
                letterSpacing: '-0.015em',
              }}
            >
              <T>{tab.title}</T>
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.muted',
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <T>{tab.meta}</T>
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: '14px', color: 'grey.600', lineHeight: 1.6, mb: 3 }}
          >
            <T>{tab.desc}</T>
          </Typography>
          <PanelContent tab={tab} />
        </Box>
      </Box>
    </PageSection>
  );
}
